import { VisuallyHidden } from 'react-aria';

import Spacer from 'design-system/components/Spacer';
import Button, { ButtonType } from 'inquiry/components/Button';
import { useStepDocumentStyleContext } from 'inquiry/context/StepDocumentStyleContext';
import { StepDocumentConfig } from 'lib/interfaces/StepsConfig';
import { DocumentFile } from 'verify/models/document-file';
import { CaptureMethod } from 'verify/models/verification';
import DocumentScanner from 'verify/scanners/DocumentScanner';

interface InquiryDocumentsCaptureViewProps {
  documentFiles: DocumentFile[];
  documentFileLimit: number;
  handleCapture: (imageDataUris: string[], captureMethod: CaptureMethod) => Promise<void>;
  handleError: (error: Error | string, state: 'load' | 'capture') => void;
  handleFinish: () => void;
  localizations: StepDocumentConfig['localizations']['capture-page'];
  videoConstraints: MediaTrackConstraints;
}

export default function InquiryDocumentsCaptureView({
  documentFiles,
  documentFileLimit,
  handleCapture,
  handleError,
  handleFinish,
  localizations,
  videoConstraints,
}: InquiryDocumentsCaptureViewProps) {
  const { buttons } = useStepDocumentStyleContext();

  const fileLimitReached = documentFiles.length >= documentFileLimit;

  if (documentFiles.length === 0) {
    return (
      <>
        <VisuallyHidden tabIndex={-1}>{localizations['prompt']}</VisuallyHidden>
        <DocumentScanner
          handleCapture={handleCapture}
          handleError={handleError}
          labelTakePhoto={localizations['btn-take-photo']}
          videoConstraints={videoConstraints}
          captures={[]}
        />
      </>
    );
  } else {
    return (
      <>
        <VisuallyHidden tabIndex={-1}>{localizations['prompt']}</VisuallyHidden>
        <DocumentScanner
          handleCapture={handleCapture}
          handleError={handleError}
          labelTakePhoto={localizations['btn-additional-photo']}
          videoConstraints={videoConstraints}
          captures={documentFiles.map((file) => file.attributes.originals[0])}
          renderButton={(captureButtonArgs) => (
            <>
              {fileLimitReached ? null : (
                <>
                  <Button
                    {...captureButtonArgs}
                    buttonType={ButtonType.Primary}
                    styles={buttons?.[ButtonType.Primary]}
                  />
                  {/* Assume spacing is specified by styles if present */}
                  {buttons == null && <Spacer height={1} />}
                </>
              )}

              <Button
                id='documents-capture__submit'
                buttonType={ButtonType.Secondary}
                styles={buttons?.[ButtonType.Secondary]}
                onClick={handleFinish}
              >
                {localizations['btn-finish']}
              </Button>
            </>
          )}
        />
      </>
    );
  }
}
